/**
 * Remembering which sidebar groups a person left open.
 *
 * The groups collapse without this file: `<details>` does the opening, the
 * closing and the keyboard on its own, and the server decides the sensible
 * default — the group holding the current page. All that is added here is that
 * the choice survives the next page.
 *
 * Worth the file because a dashboard is navigated in bursts. Somebody spends
 * an afternoon in the social modules and crosses eight groups every time they
 * move; making them reopen the same one at each page turn is the sort of
 * friction nobody reports and everybody feels.
 */

const STORE = 'bilbi.nav-groups';

/**
 * Reads the remembered set, and treats any failure as "nothing remembered".
 *
 * `localStorage` throws outright in a private window on some browsers, and
 * more often the value is simply absent or corrupted. None of that is worth an
 * error: the defaults the server rendered are already correct.
 */
function remembered(): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(STORE);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((k): k is string => typeof k === 'string'))
      : null;
  } catch {
    return null;
  }
}

function store(open: Set<string>): void {
  try {
    window.localStorage.setItem(STORE, JSON.stringify([...open]));
  } catch {
    // A preference that cannot be saved is a preference that lasts one page.
    // Nothing else about the navigation depends on it.
  }
}

export function initNavGroups(root: ParentNode = document): void {
  const groups = [...root.querySelectorAll<HTMLDetailsElement>('[data-nav-group]')];
  if (groups.length === 0) return;

  const known = remembered();

  /*
   * The server renders every group open, so a first visit shows the whole
   * catalogue. What follows only ever *closes* what this reader closed before:
   * a remembered preference is theirs, and an absent one is not a preference.
   */
  if (known) {
    for (const group of groups) {
      /*
       * The group holding the current page stays open whatever was
       * remembered. Closing it would hide the one row that says where the
       * reader is, on the page they just asked for.
       */
      if (group.querySelector('.is-active')) continue;

      const key = group.dataset.navGroup ?? '';
      group.open = known.has(key);
    }
  }

  const save = () => {
    store(new Set(groups.filter((g) => g.open).map((g) => g.dataset.navGroup ?? '')));
  };

  for (const group of groups) group.addEventListener('toggle', save);
}
